import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  MagnifyingGlass,
  SpinnerGap,
  X,
  CaretLeft,
  CaretRight,
  Crown,
  Pencil,
  Trash,
  Key,
  Eye
} from '@phosphor-icons/react';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const limit = 20;

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter, subscriptionFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: page * limit,
        limit: limit,
      });
      if (roleFilter) params.append('role', roleFilter);
      if (subscriptionFilter) params.append('subscription', subscriptionFilter);
      if (search) params.append('search', search);
      
      const { data } = await axios.get(`${API}/admin/users?${params}`, { withCredentials: true });
      setUsers(data.users);
      setTotal(data.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user and all their data?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${id}`, { withCredentials: true });
      fetchUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setShowPasswordModal(true);
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 md:p-8" data-testid="admin-users">
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl tracking-tighter">Users</h1>
        <p className="text-muted-foreground">{total} registered users</p>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 rounded-sm"
              />
            </div>
          </div>
          <select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 bg-background border border-input rounded-sm text-sm"
          >
            <option value="">All Roles</option>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          <select
            value={subscriptionFilter}
            onChange={(e) => { setSubscriptionFilter(e.target.value); setPage(0); }}
            className="px-3 py-2 bg-background border border-input rounded-sm text-sm"
          >
            <option value="">All Plans</option>
            <option value="free">Free</option>
            <option value="premium">Premium</option>
          </select>
          <Button type="submit" variant="secondary" className="rounded-sm">
            Search
          </Button>
        </form>
      </div>

      {/* Users Table */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <SpinnerGap className="w-8 h-8 text-primary animate-spin mx-auto" />
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No users found
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground">User</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground w-24">Role</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground w-28">Plan</th>
                <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground w-32">Progress</th>
                <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-muted-foreground w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-muted/50">
                  <td className="p-4">
                    <div>
                      <p className="font-medium text-sm">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={cn(
                      "text-xs px-2 py-1 rounded-sm capitalize",
                      user.role === 'admin' ? "bg-primary/20 text-primary" : "bg-muted"
                    )}>
                      {user.role}
                    </span>
                  </td>
                  <td className="p-4">
                    <span className={cn(
                      "flex items-center gap-1 text-xs px-2 py-1 rounded-sm",
                      user.subscription_tier === 'premium' 
                        ? "bg-accent/20 text-accent" 
                        : "bg-muted text-muted-foreground"
                    )}>
                      {user.subscription_tier === 'premium' && <Crown weight="fill" className="w-3 h-3" />}
                      {user.subscription_tier}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="text-xs text-muted-foreground">
                      <p>{user.progress.questions_answered} answers</p>
                      <p>{user.progress.mock_exams_passed}/{user.progress.mock_exams_taken} exams</p>
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <button
                      onClick={() => handleViewDetails(user)}
                      className="p-2 hover:bg-muted rounded-sm transition-colors text-muted-foreground hover:text-foreground"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(user)}
                      className="p-2 hover:bg-muted rounded-sm transition-colors text-muted-foreground hover:text-foreground"
                      title="Edit User"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleResetPassword(user)}
                      className="p-2 hover:bg-muted rounded-sm transition-colors text-muted-foreground hover:text-foreground"
                      title="Reset Password"
                    >
                      <Key className="w-4 h-4" />
                    </button>
                    {user.role !== 'admin' && (
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="p-2 hover:bg-destructive/10 rounded-sm transition-colors text-muted-foreground hover:text-destructive"
                        title="Delete User"
                      >
                        <Trash className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-sm"
              >
                <CaretLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages - 1}
                className="rounded-sm"
              >
                <CaretRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      {showModal && selectedUser && (
        <UserEditModal
          user={selectedUser}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); fetchUsers(); }}
        />
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && selectedUser && (
        <PasswordResetModal
          user={selectedUser}
          onClose={() => setShowPasswordModal(false)}
          onSave={() => setShowPasswordModal(false)}
        />
      )}

      {/* User Details Modal */}
      {showDetailModal && selectedUser && (
        <UserDetailModal
          userId={selectedUser.id}
          onClose={() => setShowDetailModal(false)}
        />
      )}
    </div>
  );
};

const UserEditModal = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: user.name,
    role: user.role,
    subscription_tier: user.subscription_tier,
    ai_enabled: user.ai_enabled,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(`${API}/admin/users/${user.id}`, formData, { withCredentials: true });
      onSave();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card border border-border rounded-sm w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-heading font-bold text-lg">Edit User</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-sm">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label>Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 rounded-sm"
            />
          </div>

          <div>
            <Label>Role</Label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <Label>Subscription</Label>
            <select
              value={formData.subscription_tier}
              onChange={(e) => setFormData({ ...formData, subscription_tier: e.target.value })}
              className="w-full mt-1 px-3 py-2 bg-background border border-input rounded-sm text-sm"
            >
              <option value="free">Free</option>
              <option value="premium">Premium</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="ai_enabled"
              checked={formData.ai_enabled}
              onChange={(e) => setFormData({ ...formData, ai_enabled: e.target.checked })}
              className="accent-primary"
            />
            <Label htmlFor="ai_enabled" className="cursor-pointer">AI Features Enabled</Label>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="rounded-sm">
              {saving ? <SpinnerGap className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PasswordResetModal = ({ user, onClose, onSave }) => {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    
    try {
      await axios.post(`${API}/admin/users/${user.id}/reset-password`, 
        { new_password: password }, 
        { withCredentials: true }
      );
      alert('Password reset successfully');
      onSave();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card border border-border rounded-sm w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-heading font-bold text-lg">Reset Password</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-sm">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <p className="text-sm text-muted-foreground">
            Reset password for <strong>{user.email}</strong>
          </p>
          
          <div>
            <Label>New Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="mt-1 rounded-sm"
            />
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="rounded-sm">
              {saving ? <SpinnerGap className="w-4 h-4 animate-spin" /> : 'Reset Password'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserDetailModal = ({ userId, onClose }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/users/${userId}`, { withCredentials: true });
        setUser(data);
      } catch (error) {
        console.error('Failed to fetch user details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [userId]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card border border-border rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="font-heading font-bold text-lg">User Details</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-sm">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <SpinnerGap className="w-8 h-8 text-primary animate-spin mx-auto" />
          </div>
        ) : user ? (
          <div className="p-4 space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="font-bold text-sm mb-2">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Name:</span>
                  <span className="ml-2">{user.name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Email:</span>
                  <span className="ml-2">{user.email}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Role:</span>
                  <span className="ml-2 capitalize">{user.role}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Plan:</span>
                  <span className="ml-2 capitalize">{user.subscription_tier}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Joined:</span>
                  <span className="ml-2">{new Date(user.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Progress */}
            <div>
              <h3 className="font-bold text-sm mb-2">Progress</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Questions Answered:</span>
                  <span className="ml-2">{user.progress?.total_questions_answered || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Correct Answers:</span>
                  <span className="ml-2">{user.progress?.correct_answers || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Mock Exams Taken:</span>
                  <span className="ml-2">{user.progress?.mock_exams_taken || 0}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Mock Exams Passed:</span>
                  <span className="ml-2">{user.progress?.mock_exams_passed || 0}</span>
                </div>
              </div>
            </div>

            {/* Spaced Repetition */}
            {user.spaced_repetition_stats && (
              <div>
                <h3 className="font-bold text-sm mb-2">Spaced Repetition</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">In Rotation:</span>
                    <span className="ml-2">{user.spaced_repetition_stats.total}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Mastered:</span>
                    <span className="ml-2">{user.spaced_repetition_stats.mastered}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Avg Ease:</span>
                    <span className="ml-2">{user.spaced_repetition_stats.avg_ease?.toFixed(2) || 'N/A'}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Exams */}
            {user.exam_history?.length > 0 && (
              <div>
                <h3 className="font-bold text-sm mb-2">Recent Exams</h3>
                <div className="space-y-2">
                  {user.exam_history.map((exam, idx) => (
                    <div key={idx} className="flex justify-between text-sm p-2 bg-muted rounded-sm">
                      <span>{new Date(exam.timestamp).toLocaleDateString()}</span>
                      <span>{exam.score}/{exam.total}</span>
                      <span className={exam.passed ? "text-success" : "text-destructive"}>
                        {exam.passed ? 'PASSED' : 'FAILED'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center text-muted-foreground">
            Failed to load user details
          </div>
        )}
      </div>
    </div>
  );
};
